<template>
  <div class="highcharts-light bar-chart chart-container">
    <highcharts class="chart-element" :options="chartOptions" ref="chartComponent"></highcharts>
    <footer class="pagination">
      <button :disabled="currentPage === 0" @click="prevPage">
        <i class="fa fa-chevron-up"></i>
      </button>
      <span class="page-number">
        {{ currentPage + 1 }} / {{ Math.max(1,Math.ceil(data.length / pageSize)) }}
      </span>
      <button :disabled="currentPage + 1 >= Math.ceil(data.length / pageSize)" @click="nextPage">
        <i class="fa fa-chevron-down"></i>
      </button>
    </footer>
  </div>
</template>

<script>
import {Chart} from 'highcharts-vue';
import Highcharts from 'highcharts';
import addAccessibility from 'highcharts/modules/accessibility';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import DataService from '../../../../services/data.service';

addAccessibility(Highcharts);
noDataToDisplay(Highcharts);

export default {
  name: 'bar-chart',
  components: {
    highcharts: Chart
  },
  props: {
    chartSeries: {
      type: Array,
      default: () => []
    },
    dateRange: {
      type: Object,
      default: undefined,
    },
    filter: {
      type: Object,
      default: undefined
    },
    panel: {
      type: Object,
      default: () => {}
    },
  },
  data: function () {
    this.filter.activeFiltersUpdated.add(this.renewRegistration.bind(this));
    return {
      currentPage: 0,
      data: [],
      pageSize: 5,
    }
  },
  computed: {
    chartOptions: function() {
      let self = this;
      let seriesData = self.data.slice(this.currentPage * 5, (this.currentPage + 1) * 5).map(function(item) {
        return [item.name, item.y];
      });
      // add extra rows so there is always pageSize so the size of each bar is always the same
      if (seriesData.length > 0) {
        while (seriesData.length < self.pageSize) {
          seriesData.push([' '.repeat(seriesData.length), undefined]);
        }
      }
      let cConfig = {
        chart: {
          styledMode: true,
          marginLeft: 100,
          marginRight: 20,
          height: 275
        },
        title: {
          text: undefined
        },
        lang: {
          noData: 'No Data To Display'
        },
        noData: {
          style: {
            fontSize: '14px',
            fontWeight: '400',
            color: 'var(--text-color)',
            fontFamily: 'Proxima Nova'
          }
        },
        tooltip: {
          outside: true,
          style: {
            color: 'var(--text-color)',
            fontFamily: 'Proxima Nova'
          },
          useHTML: true,
          formatter: function() {
            return `<svg width="10" height="10"><circle cx="5" cy="5" r="4"/></svg> ` + this.point.name + ': <b>' + this.point.y + '</b>';
          }
        },
        legend: {
          enabled: false
        },
        xAxis: {
          type: 'category',
          labels: {
            useHTML:true,
          }
        },
        yAxis: {
            max: this.data.length > 0 ? Math.max(...this.data.map(item => item.y)) : 1,
          tickPixelInterval: 100,
          title: {
            text: null
          },
        },
        plotOptions: {
          bar: {
            borderRadius: '50%',
            dataLabels: {
              enabled: true,
              inside: true,
              align: 'right',
              x: -6,
            },
          },
          series: {
            point: {
              events: {
                click: function (event) {
                  self.$emit('clickCallback', self.panel.groupBy, event.point.options.name);
                }
              }
            }
          },
        },
        credits: {
          enabled: false
        },
        series: [{
          type: 'bar',
          data: seriesData,
        }]
      };
      return cConfig
    }
  },
  methods: {
    nextPage: function() {
      if (this.currentPage < Math.ceil(this.data.length / this.pageSize) - 1) {
        this.currentPage++;
      }
    },
    prevPage: function() {
      if (this.currentPage > 0) {
        this.currentPage--;
      }
    },
    renewRegistration: function() {
      DataService.deregisterPanel(this.panel, this.filter, this.dateRange);
      DataService.registerPanel(this.panel, this.filter, this.dateRange, this.updateData.bind(this));
    },
    updateData: function(data) {
      this.data = data;
      let self = this;
      this.currentPage = 0;

      if (self.$refs.chartComponent.$el) {
        for (let i = 1; i < 10; i++) {
          self.$refs.chartComponent.$el.classList.remove('hc-series-' + i);
        }
        let valueLength = this.data.length;
        if (this.data && valueLength > 1) {
          self.$refs.chartComponent.$el.classList.add('hc-series-' + Math.min(10, valueLength));
        }
      }

    }
  },
  mounted() {
    let self = this;
    // We need to wait a bit to call reflow here; $nextTick seems to be the cleanest option.
    this.$nextTick(() => {
      if (self.$refs.chartComponent) {
        self.$refs.chartComponent.chart.reflow();
      }
    });
    DataService.registerPanel(this.panel, this.filter, this.dateRange, this.updateData.bind(this));
  },
  beforeDestroy() {
    DataService.deregisterPanel(this.panel, this.filter, this.dateRange);
  },
  watch: {
    dateRange: {
      handler: function(newRange, oldVal) {
        if (!newRange.startDate.isSame(oldVal.startDate) || !newRange.endDate.isSame(oldVal.endDate)) {
          this.renewRegistration();
        }
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.bar-chart {
  .chart-element {
    height: calc(100% - 40px);
  }
  .pagination {
    margin: 0;
    padding: 10px 20px;
    .page-number {
      padding: 0 20px;
    }
  }
}
</style>

<style lang="scss">
.bar-chart.chart-container {
  .highcharts-container {
    min-width: 280px;
    margin: 0 auto;
    .highcharts-legend-item span {
      font-size: 14px;
    }
    .highcharts-series {
      cursor: pointer;
    }
    .highcharts-axis-labels {
      text-align: right;
    }
    .highcharts-data-label text {
      fill: var(--chart-data-label-inside);
    }
  }
}
</style>
